import { createStore, type Store, type StoreActionApi } from 'react-sweet-state';

import { type SuggestedPromptTile } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { getPrimaryPrompts } from './getPrimaryPrompts';
import { getSecondaryPrompts } from './getSecondaryPrompts';
import type { UsePromptSuggestionsState } from './types';

const shuffleArray = <T>(items: T[]): T[] => {
	const shuffled = [...items];

	for (let i = shuffled.length - 1; i > 0; i--) {
		const randomIndex = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[i]];
	}

	return shuffled;
};

const shufflePromptBuckets = ({
	primaryPrompts,
	secondaryPrompts,
}: {
	primaryPrompts: SuggestedPromptTile[];
	secondaryPrompts: SuggestedPromptTile[];
}) => {
	const shuffledPrompts = shuffleArray([...primaryPrompts, ...secondaryPrompts]);

	return {
		primaryPrompts: getPrimaryPrompts(shuffledPrompts),
		secondaryPrompts: getSecondaryPrompts(shuffledPrompts),
	};
};

const getInitialState = (): UsePromptSuggestionsState => ({
	data: null,
	primaryPrompts: null,
	secondaryPrompts: null,
	isLoading: false,
	isPrimaryPromptsLoading: false,
	isSecondaryPromptsLoading: false,
	error: null,
});

export const store: Store<
	UsePromptSuggestionsState,
	{
		reset: () => ({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void;
		getState: () => ({
			getState,
		}: StoreActionApi<UsePromptSuggestionsState>) => Readonly<UsePromptSuggestionsState>;
		setLoading: () => ({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void;
		setPrimaryPrompts: (
			primaryPrompts: SuggestedPromptTile[],
		) => ({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void;
		setSecondaryPrompts: (
			secondaryPrompts: SuggestedPromptTile[],
		) => ({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void;
		setData: (
			data: SuggestedPromptTile[],
		) => ({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void;
		shuffleSuggestions: () => ({
			getState,
			setState,
		}: StoreActionApi<UsePromptSuggestionsState>) => void;
		setError: (error: Error) => ({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void;
	}
> = createStore({
	name: 'promptSuggestions',
	initialState: getInitialState(),
	actions: {
		reset:
			(): (({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void) =>
			({ setState }) => {
				setState(getInitialState());
			},
		getState:
			(): (({
				getState,
			}: StoreActionApi<UsePromptSuggestionsState>) => Readonly<UsePromptSuggestionsState>) =>
			({ getState }) =>
				getState(),
		setLoading:
			(): (({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void) =>
			({ setState }) => {
				setState({
					data: null,
					primaryPrompts: null,
					secondaryPrompts: null,
					isLoading: true,
					isPrimaryPromptsLoading: true,
					isSecondaryPromptsLoading: true,
					error: null,
				});
			},
		setPrimaryPrompts:
			(
				primaryPrompts: SuggestedPromptTile[],
			): (({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void) =>
			({ setState }) => {
				setState({ primaryPrompts, isPrimaryPromptsLoading: false });
			},
		setSecondaryPrompts:
			(
				secondaryPrompts: SuggestedPromptTile[],
			): (({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void) =>
			({ setState }) => {
				setState({ secondaryPrompts, isSecondaryPromptsLoading: false });
			},
		setData:
			(
				data: SuggestedPromptTile[],
			): (({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void) =>
			({ setState }) => {
				setState({ data, isLoading: false });
			},
		shuffleSuggestions:
			(): (({ getState, setState }: StoreActionApi<UsePromptSuggestionsState>) => void) =>
			({ getState, setState }) => {
				const {
					primaryPrompts,
					secondaryPrompts,
					isPrimaryPromptsLoading,
					isSecondaryPromptsLoading,
				} = getState();

				if (
					isPrimaryPromptsLoading ||
					isSecondaryPromptsLoading ||
					!primaryPrompts ||
					!secondaryPrompts
				) {
					return;
				}

				const shuffledPrompts = shufflePromptBuckets({ primaryPrompts, secondaryPrompts });
				setState(shuffledPrompts);
			},
		setError:
			(error: Error): (({ setState }: StoreActionApi<UsePromptSuggestionsState>) => void) =>
			({ setState }) => {
				setState({
					error,
					isLoading: false,
					isPrimaryPromptsLoading: false,
					isSecondaryPromptsLoading: false,
				});
			},
	},
});
